import type React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { HTTPClient } from "../../HTTPClient";
import type { Gift } from "../../types/campaign/Gift";
import type { CampaignContextType } from "../../types/campaign/CampaignContext";
import { useSnackbar } from "../../providers/ToastProvider";
import { useTranslation } from "react-i18next";
import type { Action, Campaign, CampaignConfiguration } from "@tgc/types";
import { GiftType } from "@tgc/types";
import { isActionValid } from "../../utils/actions";
import { usePlace } from "../../providers/PlaceProvider";
import { ConditionField } from "../../types/campaign/Condition";
import type { PromoCode } from "../../types/campaign/PromoCode";

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

const CampaignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation("", { keyPrefix: "views.campaign.save" });
  const { palette } = useTheme();
  const { snackbarShowMessage } = useSnackbar();
  const { selectedPlace } = usePlace();

  const [minPurchase, setMinPurchase] = useState<boolean>(false);
  const [minPurchaseAmount, setMinPurchaseAmount] = useState<string>('');
  const [conditionFields, setConditionFields] = useState<ConditionField[]>([]);
  const [skipCooldown, setSkipCooldown] = useState<boolean>(false);
  const [controlledReviews, setControlledReviews] = useState<boolean>(false);
  const [voucherExpiration, setVoucherExpiration] = useState<number | null>(null);
  const [voucherStart, setVoucherStart] = useState<number>(1);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);

  const [id, setID] = useState<string>(selectedPlace?.id || "");
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [campaign, setCampaign] = useState<CampaignConfiguration | null>(null);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const giftsRef = useRef<Gift[]>([]);
  useEffect(() => { giftsRef.current = gifts; }, [gifts]);
  const getGiftsForSaveRef = useRef<() => Gift[]>(() => giftsRef.current);
  const registerGetGiftsForSave = (fn: () => Gift[]) => { getGiftsForSaveRef.current = fn; };
  const [actions, setActions] = useState<Action[]>([]);
  const [game, setGame] = useState<string>("WHEEL");
  const [primaryColor, setPrimaryColor] = useState<string>(palette.primary.main);
  const [secondaryColor, setSecondaryColor] = useState<string>(palette.secondary.main);
  const [logo, setLogo] = useState<string>("");
  const [logoUri, setLogoUri] = useState<string>("");
  const [startDate, setStartDate] = useState<string | undefined>();
  const [label, setLabel] = useState<string>("");
  const [placeId, setPlaceId] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [profile, setProfile] = useState<string>("PREMIUM");
  const [theme, setTheme] = useState<string>("DEFAULT");
  const [kioskMode, setKioskMode] = useState<boolean>(false);
  const [wizardMode, setWizardMode] = useState<boolean>(false);

  const createCampaignConfigurationFromData = (data: Campaign) => {
    const { configuration } = data;
    setID(data.id || "");
    setCampaign(configuration);
    setGifts(configuration.gifts ?? []);
    setActions(configuration.actions ?? []);
    setGame(configuration.game_type ?? "WHEEL");
    setPrimaryColor(configuration.colors?.primary ?? palette.primary.main);
    setSecondaryColor(configuration.colors?.secondary ?? palette.secondary.main);
    setLogo("");
    setLogoUri(configuration.logo_uri ?? "");
    setStartDate(configuration.start_date);
    setLabel(data.label ?? "");
    setPlaceId(data.placeId ?? "");
    setIsDisabled(configuration.disabled ?? false);
    setProfile(configuration.profile ?? "PREMIUM");
    setTheme(configuration.theme ?? "DEFAULT");
    setKioskMode(configuration.kiosk_mode ?? false);
    setWizardMode(!configuration.actions?.length && !configuration.gifts?.length);
    setSkipCooldown((data as any).skipCooldown ?? (configuration as any).skipCooldown ?? false);
    setControlledReviews((configuration as any).controlledReviews ?? false);
    setVoucherStart((data as any).voucherStart ?? (configuration as any).voucherStart ?? 1);
    setVoucherExpiration((data as any).voucherExpiration ?? (configuration as any).voucherExpiration ?? null);
    setPromoCodes((data as any).promoCodes ?? []);
  };

  const WIZARD_STEPS = ["actions", "game", "customization", "gifts", "conditions", "date"];

  const hasInvalidGifts = (gifts: Gift[]) =>
    gifts.some(
      (g) =>
        g.type !== "LOSS" &&
        (!g.name?.trim() ||
          !g.type ||
          (String(g.id ?? "").startsWith("new-") &&
            (typeof g.limit !== "number" ||
              Number.isNaN(g.limit) ||
              (g.limit !== -1 &&
                (g.initial_limit == null || Number.isNaN(g.initial_limit) || g.initial_limit < 0)))))
    );

  const isMissingUnlimitedGift = (gifts: Gift[]) =>
    !gifts.some((g) => g.type === "LOSS") && !gifts.some((g) => g.limit && g.limit <= -1);

  const hasMissingPromoCodes = (gifts: Gift[]) => {
    const hasPromoCodeGift = gifts.some((g) => g.type === GiftType.PROMO_CODE);
    const hasAtLeastOnePromoCode = promoCodes.some((p) => p?.value && `${p.value}`.trim().length > 0);
    return hasPromoCodeGift && !hasAtLeastOnePromoCode;
  };

  const getGiftsValidationError = (currentGifts: Gift[], shouldCheck: (step: string) => boolean): string | null => {
    if (!shouldCheck("gifts")) return null;
    if (!currentGifts.length) return t("errors.gifts.empty");
    if (hasInvalidGifts(currentGifts)) return t("errors.gifts.empty_fields");
    if (isMissingUnlimitedGift(currentGifts)) return t("errors.gifts.missing_unlimited");
    if (hasMissingPromoCodes(currentGifts)) return t("errors.promoCodes.empty");
    return null;
  };

  const getValidationError = (wizardStep: number | undefined, currentGifts: Gift[]): string | null => {
    const shouldCheck = (step: string) =>
      wizardStep === undefined || wizardStep >= WIZARD_STEPS.indexOf(step);

    if (!actions.length && !campaign?.is_proxy) return t("errors.actions.empty");
    if (actions.some((a) => !isActionValid(a))) return t("errors.actions.target_missing");

    if (!campaign?.is_proxy) {
      const giftsError = getGiftsValidationError(currentGifts, shouldCheck);
      if (giftsError) return giftsError;
    }

    if (!startDate && shouldCheck("date") && wizardStep !== undefined) return t("errors.date.empty");

    return null;
  };

  const saveCampaign = ({ wizardStep, forceSave }: { wizardStep?: number; forceSave?: boolean } = {}) => {
    return new Promise<Campaign | undefined>((resolve, reject) => {
      setIsSaving(true);

      const fail = (msg: string) => {
        snackbarShowMessage(msg, "error");
        setIsSaving(false);
        reject(new Error(msg));
      };

      if (!id) return fail(t("errors.must-add-place"));

      const currentGifts = getGiftsForSaveRef.current();

      if (!forceSave) {
        const error = getValidationError(wizardStep, currentGifts);
        if (error) return fail(error);
      }

      const conditions = conditionFields
        ?.filter((c) => !c.isNoCondition && c.value && c.value.trim() !== "")
        .map((c) => ({ giftId: c.giftId, condition: c.value.trim() })) || [];

      const campaignData = {
        id,
        gifts: currentGifts.map((gift) => ({
          ...gift,
          ...(gift.id && !gift.id.startsWith("new") && { id: gift.id }),
          initial_limit: gift.initial_limit ?? gift.limit,
        })),
        actions: actions.map(({ ...action }) => ({ ...action })),
        conditions,
        game_type: game,
        primaryColor,
        secondaryColor,
        logo_uri: logoUri,
        label,
        start_date: startDate,
        disabled: isDisabled,
        profile,
        theme,
        kiosk_mode: kioskMode,
        is_proxy: campaign?.is_proxy ?? false,
        targets: campaign?.targets ?? [],
        skipCooldown,
        controlledReviews,
        voucherStart: voucherStart ?? 1,
        voucherExpiration: voucherExpiration ?? null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        promoCodes, // full list = source of truth; backend deletes codes absent from this array
      };

      HTTPClient.post(`/campaign/`, campaignData)
        .then(({ data }) => {
          resolve(data);
          snackbarShowMessage(t("success"));
        })
        .catch((err) => {
          if (err.response?.status === 413) return snackbarShowMessage(t("errors.content_too_large"), "error");
          if (err.response?.status === 403) return snackbarShowMessage(t("errors.forbidden"), "error");
          reject();
          snackbarShowMessage(t("unknown-error"), "error");
        })
        .finally(() => setIsSaving(false));
    });
  };

  const fetchCampaign = () => {
    setIsLoaded(false);
    if (!selectedPlace) return;
    console.log("Fetching campaign: " + selectedPlace.id);
    HTTPClient.get("/campaign/" + selectedPlace.id)
      .then(({ data }: { data: Campaign }) => {
        createCampaignConfigurationFromData(data);
        setIsLoaded(true);
      })
      .catch(() => setIsLoaded(true));
  };

  useEffect(() => {
    console.log("Triggered fetchCampaign");
    fetchCampaign();
  }, [selectedPlace?.id]);

  const contextValue: CampaignContextType = {
    id,
    campaign,
    gifts,
    actions,
    game,
    primaryColor,
    secondaryColor,
    logo,
    logoUri,
    startDate,
    label,
    placeId,
    isLoaded,
    isSaving,
    isDisabled,
    profile,
    wizardMode,
    setID,
    setGifts,
    setActions,
    setGame,
    setPrimaryColor,
    setSecondaryColor,
    setLogo,
    setLogoUri,
    setStartDate,
    setLabel,
    setIsLoaded,
    setIsDisabled,
    setProfile,
    setCampaign,
    saveCampaign,
    registerGetGiftsForSave,
    conditionFields,
    setConditionFields,
    minPurchase,
    setMinPurchase,
    minPurchaseAmount,
    setMinPurchaseAmount,
    skipCooldown,
    setSkipCooldown,
    controlledReviews,
    setControlledReviews,
    voucherExpiration,
    setVoucherExpiration,
    voucherStart,
    setVoucherStart,
    promoCodes,
    setPromoCodes,
    refresh: fetchCampaign,
  };

  return <CampaignContext.Provider value={contextValue}>{children}</CampaignContext.Provider>;
};

const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error("useCampaign must be used within a CampaignProvider");
  }
  return context;
};

export { CampaignProvider, useCampaign };
